import { createDynamicFeatureSelector } from '@app/app.selectors';
import { FieldKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FormField } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { PersonType, PersonUuid } from '@core/data-access/models/person.model';
import { createSelector } from '@ngrx/store';
import { defaultTo, find, get } from 'lodash';
import { SavedTicketHoldersStoreState } from '../models/saved-ticket-holders.store.model';
import { initialSavedTicketsHolderState, savedTicketHoldersFeatureKey } from './saved-ticket-holder.reducer';

const savedTicketHoldersFeatureSelector = createDynamicFeatureSelector<SavedTicketHoldersStoreState>(
  savedTicketHoldersFeatureKey,
  initialSavedTicketsHolderState
);

export const selectSavedTicketHolders = createSelector(
  savedTicketHoldersFeatureSelector,
  (savedTicketHoldersFeature: SavedTicketHoldersStoreState) => savedTicketHoldersFeature.savedTicketHolders
);

export const selectSavedTicketHolderForms = createSelector(
  savedTicketHoldersFeatureSelector,
  (savedTicketHoldersFeature: SavedTicketHoldersStoreState) => savedTicketHoldersFeature.forms
);

export const selectSavedTicketHoldersForms = createSelector(
  selectSavedTicketHolderForms,
  (savedTicketHolderForms) => savedTicketHolderForms[FormName.SavedTicketHolders]
);

export const selectSavedTicketHoldersFormByPersonUuid = (uuid: PersonUuid) =>
  createSelector(selectSavedTicketHoldersForms, (savedTicketHolderForms) => {
    const savedTicketHolderForm = savedTicketHolderForms[uuid];

    return savedTicketHolderForm;
  });

export const selectSavedTicketHoldersFormFieldsByPersonUuid = (uuid: PersonUuid) =>
  createSelector(
    selectSavedTicketHoldersFormByPersonUuid(uuid),
    (savedTicketHolderForm) => savedTicketHolderForm?.formFields
  );

export const selectCreateSavedTicketHolderForm = createSelector(
  selectSavedTicketHolderForms,
  (savedTicketHolderForms) => savedTicketHolderForms[FormName.CreateTicketHolder]
);

export const selectCreateSavedTicketHolderFormFields = createSelector(
  selectCreateSavedTicketHolderForm,
  (savedTicketHolderForm) => savedTicketHolderForm?.formFields
);

export const selectSavedTicketHoldersByPersonUuid = (uuid: PersonUuid) =>
  createSelector(selectSavedTicketHolders, (savedTicketHolderForms) => savedTicketHolderForms[uuid]);

export const selectSavedBuyer = createSelector(selectSavedTicketHolders, (savedTicketHolders) =>
  find(savedTicketHolders, (savedTicketHolder) => get(savedTicketHolder, 'person.type') === PersonType.Buyer)
);

export const selectSavedBuyerPerson = createSelector(selectSavedBuyer, (savedBuyer) => savedBuyer?.person);

export const selectSavedBuyerFormFields = createSelector(selectSavedBuyer, (savedBuyer) => savedBuyer?.formFields);

export const selectSavedBuyerEmailAddress = createSelector(selectSavedBuyerFormFields, (formFields) =>
  defaultTo((formFields[FieldKey.Email] as FormField)?.value as string, '')
);

export const selectSavedFormBuyerPerson = createSelector(
  selectSavedTicketHolders,
  (savedTicketHoldersForms) =>
    find(
      savedTicketHoldersForms,
      (savedTicketHoldersForm) => get(savedTicketHoldersForm, 'person.type') === PersonType.Buyer
    )?.person
);
